import type {
  ISectionRepository,
  MemberSections,
  PaginatedResponse,
  QueryParams,
  SectionAdminAssignment,
} from '@yawi/domain';
import { matchesSearch, paginateArray, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type {
  Section,
  SectionBankInfo,
  CreateSectionDTO,
  UpdateSectionDTO,
  SectionStatus,
  SectionStats,
  SectionAdmin,
  SectionTenantStats,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiSectionRepository implements ISectionRepository {
  /**
   * GET /sections devuelve el catálogo completo sin paginar (dropdowns),
   * ya filtrado por la sección visible del usuario.
   */
  async getAllSections(): Promise<Section[]> {
    const client = getApiClient();
    return await client.get<Section[]>('v1/sections');
  }

  async getSections(params?: QueryParams): Promise<PaginatedResponse<Section>> {
    // El endpoint no recibe búsqueda: se filtra el catálogo (ya acotado por el
    // backend) antes de paginar, para que `meta.total` describa lo filtrado.
    const search = params?.search;
    const sections = (await this.getAllSections()).filter((s) => matchesSearch(search, s.name, s.code));
    return paginateArray(sections, params?.page ?? DEFAULT_PAGE, params?.limit ?? DEFAULT_PAGE_SIZE);
  }

  async getSectionById(id: string): Promise<Section | null> {
    const client = getApiClient();
    try {
      return await client.get<Section>(`v1/sections/${id}`);
    } catch {
      return null;
    }
  }

  async getSectionStats(id: string): Promise<SectionStats> {
    const client = getApiClient();
    return await client.get<SectionStats>(`v1/sections/${id}/stats`);
  }

  async deleteSection(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete<void>(`v1/sections/${id}`);
  }

  async createSection(data: CreateSectionDTO): Promise<Section> {
    const client = getApiClient();
    return await client.post<Section>('v1/sections', data);
  }

  async updateSection(id: string, data: UpdateSectionDTO): Promise<Section> {
    const client = getApiClient();
    return await client.put<Section>(`v1/sections/${id}`, data);
  }

  async updateSectionBankInfo(id: string, bankInfo: SectionBankInfo | null): Promise<Section> {
    const client = getApiClient();
    return await client.put<Section>(`v1/sections/${id}/bank-info`, { bankInfo });
  }

  async updateSectionStatus(id: string, status: SectionStatus): Promise<Section> {
    const client = getApiClient();
    return await client.patch<Section>(`v1/sections/${id}/status`, { status });
  }

  async assignSectionAdmin(sectionId: string, memberId: string): Promise<SectionAdminAssignment> {
    const client = getApiClient();
    return await client.post<SectionAdminAssignment>(`v1/sections/${sectionId}/admins`, { memberId });
  }

  async getAdmins(sectionId: string): Promise<SectionAdmin[]> {
    const client = getApiClient();
    return await client.get<SectionAdmin[]>(`v1/sections/${sectionId}/admins`);
  }

  async removeSectionAdmin(sectionId: string, memberId: string): Promise<void> {
    const client = getApiClient();
    await client.delete<void>(`v1/sections/${sectionId}/admins/${memberId}`);
  }

  async getTenantStats(): Promise<SectionTenantStats> {
    const client = getApiClient();
    return await client.get<SectionTenantStats>('v1/sections/stats');
  }

  async getMemberSections(memberId: string): Promise<MemberSections> {
    const client = getApiClient();
    return await client.get<MemberSections>(`v1/sections/members/${memberId}`);
  }

  async setMemberSections(memberId: string, sectionIds: string[]): Promise<MemberSections> {
    const client = getApiClient();
    return await client.put<MemberSections>(`v1/sections/members/${memberId}`, { sectionIds });
  }
}